// @ts-nocheck
/*
 * Port of views/dashboard/disposition-follow-ups/list.js (embedded panel).
 * APIs: API_DISPOSITION_FOLLOW_UPS_LIST + API_DISPOSITION_FOLLOW_UP_STATUS.
 * Default range month-start→today+30, 3-month max, ACL-gated. On the agent
 * and team-lead dashboards the list uses its own scheduled-date range
 * (follow_up_date), which may extend into the future. Status filter
 * treats 'overdue' client-side (filter by effective_status).
 * Column count preserved: 7 for agent, 8 for non-agent (adds Actions).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { readPersisted, usePersistedState } from '@/lib/filters/persisted-state';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { AlarmClock, Calendar, Check, Clock, Phone, RefreshCw, X } from 'lucide-react';
import type { ColumnDef, Table as TanstackTable } from '@tanstack/react-table';
import { getApiCall, patchApiCall } from '@/service/httpCall';
import { API_DISPOSITION_FOLLOW_UPS_LIST, API_DISPOSITION_FOLLOW_UP_STATUS } from '@/service/url';
import { getUser } from '@/utilities/setting';
import { USER_ROLES } from '@/utilities/constant';
import { canUseDispositionFollowUpsFeature } from '@/utilities/dispositionFormsAcl';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { downloadCsv, stampedFileName } from '@/components/data-table/csv-export';
import StatTile from '@/features/cti/components/call-stats/StatTile';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import { CallTranscriptButton, useCanViewTranscripts, useTranscriptAvailability } from '@/features/transcripts/CallTranscript';
import MediaAudioList, { mediaUrl } from '@/components/partials/components/MediaAudioList';
import { canReadRecordings, isAgentVoicemailEnabled } from '@/utilities/recordingsAcl';
import { canCallQualityFeedback, feedbackExportColumns, feedbackOf } from '@/features/call-quality-feedback/feedback';
import { useSectionRefresh } from '@/features/cti2/components/DashboardSection';

const TZ = 'Asia/Kolkata';
const MAX_MONTHS = 3;

const formatDateOnlyLocal = (date) => {
  const off = date.getTimezoneOffset();
  return new Date(date.getTime() - off * 60000).toISOString().slice(0, 10);
};

const getDefaultRange = () => {
  const now = new Date();
  return {
    from: formatDateOnlyLocal(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: formatDateOnlyLocal(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 30)),
  };
};

const isRangeWithinLimit = (from, to) => {
  if (!from || !to) return true;
  const f = new Date(`${from}T00:00:00`);
  const maxTo = new Date(f);
  maxTo.setMonth(maxTo.getMonth() + MAX_MONTHS);
  return new Date(`${to}T00:00:00`).getTime() <= maxTo.getTime();
};

const DEFAULT_RANGE = getDefaultRange();

const normalizeTime = (v) => {
  if (!v) return '00:00:00';
  const s = String(v).trim();
  if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s;
  if (/^\d{2}:\d{2}$/.test(s)) return `${s}:00`;
  return s;
};

const getFollowUpTimestamp = (row) => {
  if (!row?.follow_up_date) return 0;
  const dateStr = String(row.follow_up_date).slice(0, 10);
  const timeStr = normalizeTime(row.follow_up_time);
  const d = new Date(`${dateStr}T${timeStr}`);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

const fmtFollowUpDateTime = (row) => {
  const ts = getFollowUpTimestamp(row);
  if (!ts) return '-';
  return new Date(ts).toLocaleString('en-IN', { timeZone: TZ, day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
};

const fmtScheduleDay = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtCreated = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN', { timeZone: TZ, day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
};

const CLOSED_STATUSES = new Set(['completed', 'cancelled']);
const FOLLOW_UP_TICK_MS = 30 * 1000;

const isMaskedNumber = (value) => /[^\d+\s-]/.test(String(value || ''));

// Coarse d/h/m split - a follow-up due "in 2h 10m" is all an agent needs.
const fmtDelta = (ms) => {
  const mins = Math.floor(Math.abs(ms) / 60000);
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (d) return h ? `${d}d ${h}h` : `${d}d`;
  if (h) return m ? `${h}h ${m}m` : `${h}h`;
  return `${m}m`;
};


const fmtFieldLabel = (key) => String(key).replace(/[_-]+/g, ' ').trim();

const fmtFieldValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.map(fmtFieldValue).join(', ') : '-';
  if (typeof value === 'object') {
    try { return JSON.stringify(value); } catch { return '-'; }
  }
  return String(value);
};

function DispositionFieldsSubRow({ row }) {
  const details = row?.disposition_details;
  const entries = Object.entries(details?.fields || {});
  if (!details) return <div className="text-xs text-muted-foreground">No disposition data saved for this follow-up.</div>;
  return (
    <div className="space-y-2 p-4">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        <span>Source: <span className="font-medium text-foreground">{details.disposition_source || '-'}</span></span>
        <span>Campaign: <span className="font-medium text-foreground">{details.campaign_name || '-'}</span></span>
        <span>Call ID: <span className="font-mono text-foreground">{details.call_sid || row?.call_sid || '-'}</span></span>
        <span>Submitted: <span className="font-medium text-foreground">{fmtCreated(details.created_at)}</span></span>
      </div>
      {entries.length ? (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-4">
          {entries.map(([key, value]) => (
            <div key={key} className="min-w-0">
              <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">{fmtFieldLabel(key)}</dt>
              <dd className="break-words text-xs text-foreground">{fmtFieldValue(value)}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <div className="text-xs text-muted-foreground">No fields saved for this disposition.</div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const key = String(status || 'pending').toLowerCase();
  const cls =
    key === 'completed' ? 'bg-emerald-100 text-emerald-700'
      : key === 'overdue' ? 'bg-red-100 text-red-700'
        : key === 'cancelled' ? 'bg-muted text-muted-foreground'
          : 'bg-amber-100 text-amber-700';
  return (
    <span className={'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ' + cls}>
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-current" />
      {key}
    </span>
  );
}

export default function DispositionFollowUpsPanel({
  onPendingCountChange,
  fromDate: fromDateProp,
  toDate: toDateProp,
  dateFilterNonce,
  status: statusProp,
  onStatusChange,
  search: searchProp,
  onSearchChange,
  filterPortalTarget,
  onCallFollowUp,
  callBlockedReason = null,
  // agents.allow_early_follow_up: the Call button also works before the scheduled time.
  allowEarlyCall = false,
  refreshToken,
  readOnly = false,
  todayOnly = false,
}: any = {}) {
  const defaultRange = DEFAULT_RANGE;
  const dateKey = 'dashboard/DispositionFollowUps';
  const todayLocal = formatDateOnlyLocal(new Date());
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  const [draftStatus, setDraftStatus] = usePersistedState('dashboard/DispositionFollowUps:draftStatus', '');
  const [draftFromDate, setDraftFromDate] = usePersistedState(`${dateKey}:draftFromDate`, defaultRange.from);
  const [draftToDate, setDraftToDate] = usePersistedState(`${dateKey}:draftToDate`, defaultRange.to);
  const [draftSearch, setDraftSearch] = usePersistedState('dashboard/DispositionFollowUps:draftSearch', '');

  const [appliedStatusState, setAppliedStatusState] = usePersistedState('dashboard/DispositionFollowUps:appliedStatus', '');
  const [appliedFromDateState, setAppliedFromDateState] = usePersistedState(`${dateKey}:appliedFromDate`, defaultRange.from);
  const [appliedToDateState, setAppliedToDateState] = usePersistedState(`${dateKey}:appliedToDate`, defaultRange.to);
  const [appliedSearchState, setAppliedSearchState] = usePersistedState('dashboard/DispositionFollowUps:appliedSearch', '');

  const isControlled = fromDateProp !== undefined;
  // Controlled mode is mounted by the agent (cti2 AgentDashboardView) and
  // team-lead (TeamDashboardView, readOnly) dashboards: keep their ranges apart.
  const scheduledKey = isControlled
    ? `dashboard/DispositionFollowUps:${readOnly ? 'team' : 'agent'}`
    : null;
  // A restored range wins over the parent's seed on mount; later parent changes still apply.
  const restoredScheduledRef = useRef(
    !!scheduledKey && readPersisted(`${scheduledKey}:scheduledFrom`) !== undefined,
  );
  // Dashboard mode keeps its own scheduled-date range. The parent dates
  // (call-stats filter, capped at today) seed it, and the user can push
  // the end into the future. The API filters on follow_up_date.
  const [scheduledFrom, setScheduledFrom] = usePersistedState(scheduledKey && `${scheduledKey}:scheduledFrom`, fromDateProp || DEFAULT_RANGE.from);
  const [scheduledTo, setScheduledTo] = usePersistedState(scheduledKey && `${scheduledKey}:scheduledTo`, toDateProp || DEFAULT_RANGE.to);

  useEffect(() => {
    if (fromDateProp === undefined) return;
    if (restoredScheduledRef.current) {
      restoredScheduledRef.current = false;
      return;
    }
    setScheduledFrom(fromDateProp || '');
    setScheduledTo(toDateProp || '');
  }, [fromDateProp, toDateProp, dateFilterNonce]);

  const appliedStatus = isControlled ? (statusProp || '') : appliedStatusState;
  const appliedFromDate = isControlled ? scheduledFrom : todayOnly ? todayLocal : appliedFromDateState;
  const appliedToDate = isControlled ? scheduledTo : todayOnly ? todayLocal : appliedToDateState;
  const appliedSearch = isControlled ? (searchProp || '') : appliedSearchState;

  const applyScheduledRange = (from, to) => {
    if (from && to && from > to) {
      toast.error('From date cannot be after to date');
      return;
    }
    if (from && to && !isRangeWithinLimit(from, to)) {
      toast.error('Date range cannot exceed 3 months');
      return;
    }
    setScheduledFrom(from);
    setScheduledTo(to);
  };

  const user = getUser();
  const isSuperAdmin = user?.role === USER_ROLES.SUPERADMIN;
  const isCallCentreAdmin = user?.role === USER_ROLES.CALL_CENTRE_ADMIN;
  const isAgent = user?.role === USER_ROLES.CALL_CENTRE_AGENT;
  // readOnly: team lead viewing mapped agents' follow-ups — Agent column, no actions.
  const showAgent = !isAgent || readOnly;
  const showActions = !isAgent && !readOnly;
  const showNextFollowUpStat = !isSuperAdmin && !isCallCentreAdmin;

  const canAccess = useMemo(() => {
    if (!user) return false;
    if (isSuperAdmin) return true;
    if (isAgent) return true;
    const acl = user?.acl_settings?.module_operations
      ? parseModuleOperations(user.acl_settings.module_operations)
      : {};
    return canUseDispositionFollowUpsFeature(acl, { isSuperAdmin: false });
  }, [user, isSuperAdmin, isAgent]);

  const fetchSeqRef = useRef(0);
  const fetchData = useCallback((status, fromDate, toDate, silent = false) => {
    const seq = ++fetchSeqRef.current;
    if (!silent) {
      setLoading(true);
      setError('');
    }
    const params = {};
    if (status && status !== 'overdue') params.status = status;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    return getApiCall(API_DISPOSITION_FOLLOW_UPS_LIST, params)
      .then((resp) => {
        if (seq !== fetchSeqRef.current) return;
        let list = Array.isArray(resp?.follow_ups) ? resp.follow_ups : [];
        if (status === 'overdue') list = list.filter((r) => r.effective_status === 'overdue');
        setError('');
        setAllRows(list);
      })
      .catch((err) => {
        if (silent || seq !== fetchSeqRef.current) return;
        setError(typeof err === 'string' ? err : 'Failed to load follow-ups');
        setAllRows([]);
      })
      .finally(() => {
        if (seq === fetchSeqRef.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!canAccess) { setLoading(false); return undefined; }
    fetchData(appliedStatus, appliedFromDate, appliedToDate);
    return () => { fetchSeqRef.current += 1; }; // drop the reply if filters change or the panel unmounts
  }, [canAccess, fetchData, appliedStatus, appliedFromDate, appliedToDate, refreshToken]);

  const filteredRows = useMemo(() => {
    const q = appliedSearch.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter((row) => {
      const hay = [row.agent_name, row.agent_id, row.contact_name, row.did_number, row.disposition_form_name]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [allRows, appliedSearch]);

  const stats = useMemo(() => {
    const pending = filteredRows.filter((r) => (r.effective_status || r.status) === 'pending' || r.effective_status === 'overdue').length;
    const completed = filteredRows.filter((r) => r.status === 'completed').length;
    const upcoming = showNextFollowUpStat
      ? filteredRows.filter((r) => r.status === 'pending')
        .map((r) => ({ row: r, ts: getFollowUpTimestamp(r) }))
        .filter((x) => x.ts >= Date.now())
        .sort((a, b) => a.ts - b.ts)[0]
      : null;
    return {
      total: filteredRows.length,
      pending,
      completed,
      nextFollowUp: upcoming ? fmtFollowUpDateTime(upcoming.row) : '—',
    };
  }, [filteredRows, showNextFollowUpStat]);
  useEffect(() => {
    onPendingCountChange?.(stats.pending);
  }, [stats.pending, onPendingCountChange]);

  const applyFilters = () => {
    if (todayOnly) {
      setAppliedStatusState(draftStatus);
      setAppliedSearchState(draftSearch);
      return;
    }
    if (draftFromDate && draftToDate && draftFromDate > draftToDate) {
      toast.error('From date cannot be after to date'); return;
    }
    if (draftFromDate && draftToDate && !isRangeWithinLimit(draftFromDate, draftToDate)) {
      toast.error('Date range cannot exceed 3 months'); return;
    }
    setAppliedStatusState(draftStatus);
    setAppliedFromDateState(draftFromDate);
    setAppliedToDateState(draftToDate);
    setAppliedSearchState(draftSearch);
  };

  const resetFilters = () => {
    const { from, to } = getDefaultRange();
    setDraftStatus(''); setDraftFromDate(from); setDraftToDate(to); setDraftSearch('');
    setAppliedStatusState(''); setAppliedFromDateState(from); setAppliedToDateState(to); setAppliedSearchState('');
  };

  const refresh = () => fetchData(appliedStatus, appliedFromDate, appliedToDate);
  const inSection = useSectionRefresh(() => (canAccess ? fetchData(appliedStatus, appliedFromDate, appliedToDate, true) : undefined));

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      await patchApiCall(API_DISPOSITION_FOLLOW_UP_STATUS.replace(':id', id), { status });
      toast.success(status === 'completed' ? 'Follow-up marked completed' : 'Follow-up cancelled');
      refresh();
    } catch (err) {
      toast.error(friendlyApiError(err) || 'Unable to update follow-up');
    } finally {
      setUpdatingId(null);
    }
  };

  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNowTs(Date.now()), FOLLOW_UP_TICK_MS);
    return () => clearInterval(timer);
  }, []);


  const canViewTranscripts = useCanViewTranscripts();
  const transcriptLineCount = useTranscriptAvailability(
    useMemo(() => (filteredRows || []).map((row) => row?.trace_id), [filteredRows]),
  );

  const mediaCol = (kind, header): ColumnDef<any> => ({
    id: kind,
    accessorFn: (row) => ((row?.media || []).some((item) => item?.kind === kind) ? header : ''),
    header,
    cell: ({ row }) => {
      const media = (row.original?.media || []).filter((item) => item?.kind === kind);
      // Native seekable player; MediaAudioList stops click bubbling (row expand).
      return <MediaAudioList media={media} compact />;
    },
  });

  const columns = useMemo<ColumnDef<any>[]>(
    () => {
      const cols: ColumnDef<any>[] = [];
      if (showAgent) {
        cols.push({
          accessorKey: 'agent_name',
          header: 'Agent',
          cell: ({ row }) => (
            <div>
              <div>{row.original.agent_name || '—'}</div>
              <div className="text-xs text-muted-foreground">Agent ID: {row.original.agent_id}</div>
            </div>
          ),
        });
      }
      cols.push({
        accessorKey: 'did_number',
        header: 'Contact',
        cell: ({ row }) => (
          <div>
            {row.original.contact_name ? (
              <div className="font-medium text-foreground">{row.original.contact_name}</div>
            ) : null}
            <div className={row.original.contact_name ? 'text-xs text-muted-foreground' : undefined}>
              {row.original.did_number || '—'}
            </div>
          </div>
        ),
      });
      if (onCallFollowUp) {
        cols.push({
          id: 'call',
          header: 'Call',
          enableSorting: false,
          size: 90,
          cell: ({ row }) => {
            const followUp = row.original;
            const statusKey = String(followUp.status || '').toLowerCase();
            const ts = getFollowUpTimestamp(followUp);
            const due = followUp.effective_status === 'overdue' || (Boolean(ts) && ts <= nowTs);
            // Before the scheduled time only with the agent's early-call permission. Due, overdue
            // or early: only while the agent is idle (no call / break / wrap-up / disposition).
            const early = !due && allowEarlyCall;
            const disabled = statusKey !== 'pending'
              || (!due && !allowEarlyCall)
              || Boolean(callBlockedReason)
              || !followUp.did_number;

            let title = 'Call this follow-up';
            if (statusKey !== 'pending') title = 'Only pending follow-ups can be called';
            else if (!followUp.did_number) title = 'No number on this follow-up';
            else if (!due && !allowEarlyCall) title = `Scheduled for ${fmtFollowUpDateTime(followUp)}`;
            else if (callBlockedReason) title = callBlockedReason;
            else if (early) title = `Call early (scheduled for ${fmtFollowUpDateTime(followUp)})`;

            return (
              <Button
                size="sm"
                variant="outline"
                disabled={disabled}
                title={title}
                className="h-7 text-xs"
                onClick={(e) => { e.stopPropagation(); onCallFollowUp(followUp); }}
              >
                <Phone className="mr-1 h-3 w-3" />Call
              </Button>
            );
          },
        });
      }
      cols.push({ accessorKey: 'disposition_form_name', header: 'Disposition Form', cell: ({ row }) => row.original.disposition_form_name || '—' });
      cols.push({
        id: 'campaign_name',
        accessorFn: (row) => row?.disposition_details?.campaign_name || '',
        header: 'Campaign',
        cell: ({ getValue }) => getValue() || '—',
      });
      cols.push({
        id: 'follow_up',
        header: 'Follow-Up',
        sortingFn: (a, b) => getFollowUpTimestamp(a.original) - getFollowUpTimestamp(b.original),
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{fmtFollowUpDateTime(row.original)}</span>
        ),
      });
      cols.push({
        id: 'due_in',
        accessorFn: (row) => getFollowUpTimestamp(row),
        header: 'Overdue By / Due In',
        sortingFn: (a, b) => getFollowUpTimestamp(a.original) - getFollowUpTimestamp(b.original),
        cell: ({ row }) => {
          const ts = getFollowUpTimestamp(row.original);
          if (!ts) return <span className="text-muted-foreground">—</span>;
          const statusKey = String(row.original.status || '').toLowerCase();
          if (CLOSED_STATUSES.has(statusKey)) return <span className="text-muted-foreground">—</span>;
          const diff = ts - nowTs;
          const overdue = diff < 0;
          return (
            <span className={'inline-flex items-center gap-1 text-xs font-medium ' + (overdue ? 'text-red-600' : 'text-foreground')}>
              <AlarmClock className="h-3 w-3" />
              {overdue ? 'Overdue by ' + fmtDelta(diff) : 'Due in ' + fmtDelta(diff)}
            </span>
          );
        },
      });
      cols.push({
        id: 'status',
        header: 'Status',
        cell: ({ row }) => <StatusBadge status={row.original.effective_status || row.original.status} />,
      });
      cols.push({
        id: 'call_sid',
        accessorFn: (row) => row?.call_sid || row?.disposition_details?.call_sid || '',
        header: 'Call ID',
        cell: ({ getValue }) => {
          const value = getValue();
          if (!value) return <span className="text-muted-foreground">—</span>;
          return <span className="font-mono text-[11px]" title={value}>{value}</span>;
        },
      });
      cols.push({
        id: 'trace_id',
        accessorFn: (row) => row?.trace_id || '',
        header: 'Trace ID',
        cell: ({ getValue }) => {
          const value = getValue();
          if (!value) return <span className="text-muted-foreground">—</span>;
          return <span className="font-mono text-[11px]" title={value}>{value}</span>;
        },
      });
      // Recording column: Recordings `read` ACL. Voicemail column: the agent's
      // own is_voicemail_enabled (non-agent viewers aren't limited by it).
      if (canReadRecordings()) cols.push(mediaCol('recording', 'Recording'));
      if (!isAgent || isAgentVoicemailEnabled()) cols.push(mediaCol('voicemail', 'Voicemail'));
      if (canViewTranscripts) cols.push({
        id: 'transcript',
        accessorFn: (row) => transcriptLineCount(row?.trace_id),
        header: 'Transcript',
        size: 90,
        cell: ({ row }) => {
          const lineCount = transcriptLineCount(row.original?.trace_id);
          if (!lineCount) return <span className="text-muted-foreground">—</span>;
          return (
            <CallTranscriptButton
              traceId={row.original?.trace_id}
              lineCount={lineCount}
              className="h-7 w-7"
            />
          );
        },
      });
      cols.push({
        id: 'created_at',
        header: 'Created',
        sortingFn: (a, b) => (a.original.created_at ? new Date(a.original.created_at).getTime() : 0) - (b.original.created_at ? new Date(b.original.created_at).getTime() : 0),
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{fmtCreated(row.original.created_at)}</span>
        ),
      });
      if (showActions) {
        cols.push({
          id: 'actions',
          header: 'Actions',
          enableSorting: false,
          cell: ({ row }) => {
            const isPending = row.original.status === 'pending';
            if (!isPending) return <span className="text-muted-foreground">—</span>;
            return (
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" disabled={updatingId === row.original.id} onClick={(e) => { e.stopPropagation(); updateStatus(row.original.id, 'completed'); }} className="h-7 text-xs">
                  <Check className="mr-1 h-3 w-3" />Complete
                </Button>
                <Button size="sm" variant="outline" disabled={updatingId === row.original.id} onClick={(e) => { e.stopPropagation(); updateStatus(row.original.id, 'cancelled'); }} className="h-7 text-xs">
                  <X className="mr-1 h-3 w-3" />Cancel
                </Button>
              </div>
            );
          },
        });
      }
      return cols;
    },
    [isAgent, showAgent, showActions, updatingId, transcriptLineCount, canViewTranscripts, nowTs, onCallFollowUp, callBlockedReason, allowEarlyCall],
  );

  const exportFollowUps = (table: TanstackTable<any>) => {
    const rows = table.getPrePaginationRowModel().rows.map((r) => r.original);
    if (!rows.length) { toast.info('No follow-ups to export'); return; }
    const showRecording = canReadRecordings();
    const showVoicemail = !isAgent || isAgentVoicemailEnabled();
    // Backend attaches call_quality_feedback to follow-up rows only for feedback `read`.
    const showFeedback = canCallQualityFeedback('read');
    const now = Date.now();
    const text = (v) => (v === null || v === undefined || v === '' ? '-' : String(v));
    const timeText = (v) => {
      if (!v) return '-';
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? String(v) : `="${d.toLocaleString('sv-SE', { timeZone: TZ })}"`;
    };
    const mediaUrls = (row, kind) => {
      const urls = (row?.media || []).filter((m) => m?.kind === kind).map(mediaUrl).filter(Boolean);
      return urls.length ? urls.join(' | ') : '-';
    };

    const header = [
      'Follow-Up ID',
      ...(showAgent ? ['Agent Name', 'Agent ID'] : []),
      'Contact', 'Contact Name', 'Disposition Form', 'Campaign', 'Follow-Up Date & Time', 'Status',
      'Overdue By / Due In', 'Outcome', 'Disposition Fields', 'Disposition Submitted',
      ...(showFeedback ? Object.keys(feedbackExportColumns([])) : []),
      ...(showRecording ? ['Recording'] : []),
      ...(showVoicemail ? ['Voicemail'] : []),
      'Call SID', 'Trace ID', 'Created', 'Updated',
    ];

    const lines = rows.map((row) => {
      const details = row?.disposition_details || {};
      const fields = details.fields || {};
      const outcome = Array.isArray(fields.remarks_status) ? fields.remarks_status.filter(Boolean).join(', ') : fields.remarks_status;
      const otherFields = Object.entries(fields)
        .filter(([key, value]) => key !== 'remarks_status' && value !== null && value !== undefined && value !== '')
        .map(([key, value]) => `${fmtFieldLabel(key)}: ${fmtFieldValue(value)}`)
        .join('; ');
      const status = String(row.effective_status || row.status || '').toLowerCase();
      const ts = getFollowUpTimestamp(row);
      const dueText = !ts || CLOSED_STATUSES.has(String(row.status || '').toLowerCase())
        ? '-'
        : (ts < now ? 'Overdue by ' : 'Due in ') + fmtDelta(ts - now);
      const followUpAt = row.follow_up_date
        ? `="${String(row.follow_up_date).slice(0, 10)} ${normalizeTime(row.follow_up_time).slice(0, 5)}"`
        : '-';
      return [
        text(row.id),
        ...(showAgent ? [text(row.agent_name), text(row.agent_id)] : []),
        text(row.did_number),
        text(row.contact_name),
        text(row.disposition_form_name),
        text(details.campaign_name),
        followUpAt,
        status ? status.charAt(0).toUpperCase() + status.slice(1) : '-',
        dueText,
        text(outcome),
        text(otherFields),
        timeText(details.created_at),
        ...(showFeedback ? Object.values(feedbackExportColumns(feedbackOf(row))) : []),
        ...(showRecording ? [mediaUrls(row, 'recording')] : []),
        ...(showVoicemail ? [mediaUrls(row, 'voicemail')] : []),
        text(row.call_sid || details.call_sid),
        text(row.trace_id),
        timeText(row.created_at),
        timeText(row.updated_at),
      ];
    });

    downloadCsv([header, ...lines], stampedFileName('followups'));
  };

  if (!canAccess) return null;

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        {!isControlled ? (
          <div>
            <h1 className="flex items-center gap-2 text-sm font-bold text-foreground">
              Disposition Follow-Ups
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">{stats.total}</span>
              {todayOnly && (
                <span className="rounded-full border border-border px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">Today only</span>
              )}
            </h1>
            <p className="text-xs text-muted-foreground">Scheduled callbacks from disposition submissions</p>
          </div>
        ) : stats.total > 0 ? (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">{stats.total} total</span>
        ) : (
          <span />
        )}
        {!inSection ? (
          <Button size="sm" variant="outline" onClick={refresh} disabled={loading}>
            <RefreshCw className={'mr-1.5 h-3.5 w-3.5 ' + (loading ? 'animate-spin' : '')} />
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        ) : null}
      </div>
      {!isControlled ? (
        <div className="sticky top-10 z-[5] -mx-3 mb-3 border-b border-border/70 bg-card/95 px-3 py-3 backdrop-blur">
          <DateRangeFilter
            compact
            hideDates={todayOnly}
            value={{ from: draftFromDate, to: draftToDate }}
            onChange={({ from, to }) => { setDraftFromDate(from); setDraftToDate(to); }}
            onApply={applyFilters}
            applyLabel="Apply"
            onReset={resetFilters}
          >
            <div>
              <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Status</label>
              <select value={draftStatus} onChange={(e) => setDraftStatus(e.target.value)} className="h-9 w-full min-w-[140px] rounded-md border border-border bg-background px-2 text-sm">
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="overdue">Overdue</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="min-w-[200px] flex-1">
              <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Search</label>
              <Input
                value={draftSearch}
                onChange={(e) => setDraftSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                placeholder="Search agent, contact or form..."
                className="h-9"
              />
            </div>
          </DateRangeFilter>
        </div>
      ) : (
        filterPortalTarget &&
        createPortal(
          <DateRangeFilter
            variant="pill"
            label="Scheduled"
            calendarClassName="z-[1100]"
            value={{ from: scheduledFrom || '', to: scheduledTo || '' }}
            onChange={({ from, to }) => applyScheduledRange(from, to)}
          >
            <select
              value={appliedStatus}
              onChange={(e) => onStatusChange?.(e.target.value)}
              className="h-8 rounded-full border border-border bg-muted/60 px-3 text-[11px]"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <input
              type="search"
              value={appliedSearch}
              onChange={(e) => onSearchChange?.(e.target.value)}
              placeholder="Search agent, contact or form…"
              className="h-8 rounded-full border border-border bg-muted/60 px-3 text-[11px] outline-none"
              style={{ width: 200 }}
            />
          </DateRangeFilter>,
          filterPortalTarget,
        )
      )}

      {isControlled ? (
        <div className="mb-3 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
          <p className="col-span-full text-[11px] text-muted-foreground">
            Showing follow-ups scheduled {fmtScheduleDay(scheduledFrom)} – {fmtScheduleDay(scheduledTo)}.
          </p>
          <StatTile label="Total Follow-Ups" value={stats.total} />
          <StatTile label="Pending" value={stats.pending} tone="warn" />
          <StatTile label="Completed" value={stats.completed} tone="good" />
          {showNextFollowUpStat && <StatTile label="Next Follow-Up" value={stats.nextFollowUp} />}
        </div>
      ) : (
        <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
          <div className="shadow-neu-raised rounded-lg bg-card p-3 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
            <div className="text-xs text-muted-foreground">Total Follow-Ups</div>
            <div className="text-xl font-bold text-foreground">{stats.total}</div>
          </div>
          <div className="shadow-neu-raised rounded-lg bg-card p-3 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
            <div className="text-xs text-muted-foreground">Pending</div>
            <div className="text-xl font-bold text-amber-600">{stats.pending}</div>
          </div>
          <div className="shadow-neu-raised rounded-lg bg-card p-3 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
            <div className="text-xs text-muted-foreground">Completed</div>
            <div className="text-xl font-bold text-emerald-600">{stats.completed}</div>
          </div>
          {showNextFollowUpStat && (
            <div className="shadow-neu-raised rounded-lg bg-card p-3 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
              <div className="text-xs text-muted-foreground">Next Follow-Up</div>
              <div className="text-sm font-bold text-foreground">{stats.nextFollowUp}</div>
            </div>
          )}
        </div>
      )}

      {error ? <div className="mb-2 text-sm text-destructive">{error}</div> : null}

      <DataTable
        tableId="dashboard.disposition-follow-ups.v2"
        columns={columns}
        data={filteredRows}
        isLoading={loading}
        emptyTitle="No follow-ups"
        emptyDescription="No follow-ups found for the selected filters."
        pageSize={10}
        exportFilename="disposition-follow-ups.csv"
        onExport={exportFollowUps}
        renderSubRow={(row) => <DispositionFieldsSubRow row={row} />}
      />
    </>
  );
}
